import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Users, Gift, ArrowRight, Trophy, Copy, ExternalLink } from "lucide-react";
import { Progress } from "@/components/ui/progress";
import { toast } from "sonner";
import confetti from "canvas-confetti";
import { Link } from "react-router-dom";

export const RussianReferFriend = () => {
  const [referrals, setReferrals] = useState(2);
  const maxReferrals = 5;
  const progress = (referrals / maxReferrals) * 100;

  useEffect(() => {
    if (referrals >= maxReferrals) {
      const duration = 3 * 1000;
      const animationEnd = Date.now() + duration;
      const defaults = { startVelocity: 30, spread: 360, ticks: 60, zIndex: 0 };

      const randomInRange = (min: number, max: number) => Math.random() * (max - min) + min;

      const interval: any = setInterval(function() {
        const timeLeft = animationEnd - Date.now();

        if (timeLeft <= 0) {
          return clearInterval(interval);
        }

        const particleCount = 50 * (timeLeft / duration);
        confetti(Object.assign({}, defaults, { particleCount, origin: { x: randomInRange(0.1, 0.3), y: Math.random() - 0.2 } }));
        confetti(Object.assign({}, defaults, { particleCount, origin: { x: randomInRange(0.7, 0.9), y: Math.random() - 0.2 } }));
      }, 250);
    }
  }, [referrals]);

  const handleCopyLink = () => {
    navigator.clipboard.writeText("https://uplevelcleaning.com/ref/user123");
    toast.success("Реферальная ссылка скопирована!");
  };
  return (
    <section className="py-20 bg-muted/30 dark:bg-slate-900/20">
      <div className="container px-4">
        <div className="max-w-4xl mx-auto">
          <Card className="overflow-hidden border-none shadow-xl bg-background dark:bg-white">
            <div className="grid md:grid-cols-2">
              <div className="p-8 md:p-12 flex flex-col justify-center">
                <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-primary/10 text-primary dark:text-slate-900 text-sm font-semibold mb-6 w-fit">
                  <Users className="w-4 h-4" />
                  <span>Реферальная программа</span>
                </div>
                <h2 className="text-3xl md:text-4xl font-bold tracking-tight mb-4 text-foreground dark:text-slate-900">
                  Порекомендуй друга —{" "}
                  <span className="text-primary dark:text-slate-900">получи вознаграждение.</span>
                </h2>
                <p className="text-muted-foreground dark:text-slate-600 text-lg mb-8">
                  Довольны чистотой в вашем доме? Порекомендуйте Uplevel Cleaning другу и оба получите скидку $20 на следующий заказ. Количество рекомендаций не ограничено!
                </p>
                <div className="flex flex-col sm:flex-row gap-4">
                  <Button 
                    size="lg" 
                    className="bg-primary text-primary-foreground dark:text-white hover:bg-primary-hover font-semibold"
                    onClick={handleCopyLink}
                  >
                    <Copy className="mr-2 h-4 w-4" />
                    Скопировать ссылку
                  </Button>
                  <Button 
                    size="lg" 
                    variant="outline"
                    className="font-semibold dark:border-slate-200 dark:text-slate-900"
                    asChild
                  >
                    <Link to="/ru/referral">
                      Мои рефералы
                      <ExternalLink className="ml-2 h-4 w-4" />
                    </Link>
                  </Button>
                </div>
              </div>
              <div className="relative bg-primary hidden md:flex flex-col items-center justify-center p-10 overflow-hidden">
                <div className="absolute inset-0 opacity-10">
                  <svg className="w-full h-full" viewBox="0 0 100 100" preserveAspectRatio="none">
                    <path d="M0 0 L100 100 M100 0 L0 100" stroke="white" strokeWidth="0.5" />
                  </svg>
                </div>
                <div className="relative z-10 w-full max-w-sm text-center text-white">
                  <div className="bg-white/20 backdrop-blur-sm rounded-full p-4 mb-6 mx-auto inline-flex items-center justify-center">
                    <Trophy className="w-12 h-12 text-white" />
                  </div>
                  
                  <div className="space-y-4 bg-white/10 backdrop-blur-md p-6 rounded-2xl border border-white/20 shadow-xl">
                    <div className="flex justify-between items-end">
                      <div className="text-left">
                        <p className="text-sm font-medium text-white/80 mb-1">Ваш прогресс</p>
                        <p className="text-2xl font-bold">{referrals} / {maxReferrals} рефералов</p>
                      </div>
                      <div className="text-right">
                        <p className="text-sm font-medium text-white/80 mb-1">Награда</p>
                        <p className="text-xl font-bold flex items-center gap-1"><Gift className="w-5 h-5"/> Бесплатно</p>
                      </div>
                    </div>
                    
                    <div className="relative pt-2">
                      <Progress value={progress} className="h-3 bg-white/20 [&>div]:bg-white" />
                      <div className="flex justify-between mt-2 px-1">
                        {[1, 2, 3, 4, 5].map((step) => (
                          <div key={step} className="flex flex-col items-center">
                            <div className={`w-3 h-3 rounded-full ${step <= referrals ? 'bg-white' : 'bg-white/30'} mb-1 transition-colors duration-500`} />
                            <span className="text-[10px] font-bold text-white/70">{step}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                    
                    <p className="text-sm text-white/90 font-medium mt-4">
                      Еще {maxReferrals - referrals} для бесплатной уборки!
                    </p>
                  </div>
                </div>
                {/* Decorative circles */}
                <div className="absolute -bottom-10 -right-10 w-40 h-40 bg-white/10 rounded-full blur-3xl"></div>
                <div className="absolute -top-10 -left-10 w-40 h-40 bg-white/10 rounded-full blur-3xl"></div>
              </div>
            </div>
          </Card>

          <div className="mt-6 md:mt-16 flex flex-col md:grid md:grid-cols-3 gap-3 md:gap-8">
            <div className="flex md:flex-col items-center md:items-start md:text-center text-left bg-white dark:bg-white p-4 md:p-6 rounded-xl md:rounded-2xl shadow-sm border dark:border-slate-200 gap-4 md:gap-0">
              <div className="w-10 h-10 md:w-12 md:h-12 flex-shrink-0 bg-primary/10 text-primary dark:text-slate-900 rounded-full flex items-center justify-center md:mx-auto md:mb-4 font-bold text-lg md:text-xl">1</div>
              <div className="flex flex-col h-full justify-between w-full">
                <div>
                  <h3 className="font-bold text-base md:text-lg mb-0.5 md:mb-2 dark:text-slate-900">Получите ссылку</h3>
                  <p className="text-muted-foreground dark:text-slate-600 text-xs md:text-sm mb-3">Скопируйте вашу уникальную ссылку для друзей.</p>
                </div>
                <Button size="sm" variant="secondary" onClick={handleCopyLink} className="w-full mt-auto md:mt-2 dark:text-slate-900 dark:bg-slate-100">
                  <Copy className="w-3 h-3 mr-2" /> Копировать
                </Button>
              </div>
            </div>
            <div className="flex md:flex-col items-center md:items-start md:text-center text-left bg-white dark:bg-white p-4 md:p-6 rounded-xl md:rounded-2xl shadow-sm border dark:border-slate-200 gap-4 md:gap-0">
              <div className="w-10 h-10 md:w-12 md:h-12 flex-shrink-0 bg-primary/10 text-primary dark:text-slate-900 rounded-full flex items-center justify-center md:mx-auto md:mb-4 font-bold text-lg md:text-xl">2</div>
              <div>
                <h3 className="font-bold text-base md:text-lg mb-0.5 md:mb-2 dark:text-slate-900">Поделитесь с друзьями</h3>
                <p className="text-muted-foreground dark:text-slate-600 text-xs md:text-sm">Отправьте ссылку друзьям. Они получат скидку $20 на первую уборку.</p>
              </div>
            </div>
            <div className="flex md:flex-col items-center md:items-start md:text-center text-left bg-white dark:bg-white p-4 md:p-6 rounded-xl md:rounded-2xl shadow-sm border dark:border-slate-200 gap-4 md:gap-0">
              <div className="w-10 h-10 md:w-12 md:h-12 flex-shrink-0 bg-primary/10 text-primary dark:text-slate-900 rounded-full flex items-center justify-center md:mx-auto md:mb-4 font-bold text-lg md:text-xl">3</div>
              <div>
                <h3 className="font-bold text-base md:text-lg mb-0.5 md:mb-2 dark:text-slate-900">Заработайте $20</h3>
                <p className="text-muted-foreground dark:text-slate-600 text-xs md:text-sm">Когда они завершат уборку, вы получите скидку $20 на следующий заказ.</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};
